import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  PROJECT_FIELD_LIMITS,
  Project,
  DEFAULT_PROJECT_MAX_PAGE_SIZE,
} from "@amzn/afxtaco-types";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Search,
  Grid3X3,
  List,
  Plus,
  Sun,
  Moon,
  Folder,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useProjects } from "../hooks/useProjects";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { ProjectThumbnail } from "./ProjectThumbnail";
import Footer from "./Footer";

enum ProjectAction {
  Rename = "rename",
  Delete = "delete",
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "Unknown";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  const [searchTerm, setSearchTerm] = React.useState("");
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  // Get projects from Redux store (already loaded by DataProvider)
  const {
    projects: reduxProjects,
    isLoading,
    error,
    fetchProjects,
    nextToken,
    allProjectsLoaded,
    searchProjects,
    updateProject,
    deleteProject,
  } = useProjects();

  // Force-refresh the project list once when this page mounts so it is always up-to-date
  // fetchProjects is recreated each render by useProjects, so we use a ref to avoid an infinite fetch loop.
  const fetchProjectsRef = useRef(fetchProjects);
  fetchProjectsRef.current = fetchProjects;

  useEffect(() => {
    fetchProjectsRef.current(true);
  }, []);

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [projectToRename, setProjectToRename] = React.useState<Project | null>(
    null,
  );
  const [newProjectName, setNewProjectName] = React.useState("");
  const [newProjectDescription, setNewProjectDescription] = React.useState("");
  const [nameMaxLength, setNameMaxLength] = React.useState<number>(
    PROJECT_FIELD_LIMITS.projectName,
  );
  const [descMaxLength, setDescMaxLength] = React.useState<number>(
    PROJECT_FIELD_LIMITS.projectDescription,
  );

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [projectToDelete, setProjectToDelete] = React.useState<Project | null>(
    null,
  );

  const allProjects = reduxProjects;

  const [currentPage, setCurrentPage] = React.useState<number>(0);

  // Server-side search results (used when not all projects are loaded)
  const [serverSearchResults, setServerSearchResults] = React.useState<
    Project[] | null
  >(null);
  const [serverSearchNextToken, setServerSearchNextToken] = React.useState<
    string | null
  >(null);
  const [isSearching, setIsSearching] = React.useState(false);

  // Stable ref for searchProjects so the debounce effect doesn't re-fire
  const searchProjectsRef = useRef(searchProjects);
  searchProjectsRef.current = searchProjects;

  // Debounced server-side search when not all projects are in memory
  useEffect(() => {
    // If all projects are loaded, we filter in-memory — clear any stale server results
    if (allProjectsLoaded) {
      setServerSearchResults(null);
      setServerSearchNextToken(null);
      return;
    }

    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setServerSearchResults(null);
      setServerSearchNextToken(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await searchProjectsRef.current(trimmed);
        if (!cancelled) {
          // Fresh search — replace (don't append)
          setServerSearchResults(result.projects);
          setServerSearchNextToken(result.nextToken ?? null);
        }
      } catch {
        // Error already dispatched to Redux by the hook
        if (!cancelled) setServerSearchResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setIsSearching(false);
    };
  }, [searchTerm, allProjectsLoaded]);

  const handleAction = async (
    action: ProjectAction,
    projectId: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    const project = filteredProjects.find((p) => p.projectId === projectId);
    if (!project) return;

    switch (action) {
      case ProjectAction.Rename:
        setProjectToRename(project);
        setNewProjectName(project.projectName || "");
        setNewProjectDescription(project.projectDescription || "");
        setNameMaxLength(
          Math.max(
            PROJECT_FIELD_LIMITS.projectName,
            project.projectName?.length ?? 0,
          ),
        );
        setDescMaxLength(
          Math.max(
            PROJECT_FIELD_LIMITS.projectDescription,
            project.projectDescription?.length ?? 0,
          ),
        );
        setRenameDialogOpen(true);
        break;
      case ProjectAction.Delete:
        setProjectToDelete(project);
        setDeleteDialogOpen(true);
        break;
    }
  };

  const handleRenameConfirm = async () => {
    if (!projectToRename || !newProjectName.trim()) return;

    try {
      await updateProject(projectToRename.projectId, {
        projectName: newProjectName.trim(),
        projectDescription: newProjectDescription.trim(),
      });

      // Keep server-side search results in sync
      setServerSearchResults((prev) =>
        prev
          ? prev.map((p) =>
              p.projectId === projectToRename.projectId
                ? {
                    ...p,
                    projectName: newProjectName.trim(),
                    projectDescription: newProjectDescription.trim(),
                  }
                : p,
            )
          : prev,
      );

      setRenameDialogOpen(false);
      setProjectToRename(null);
      setNewProjectName("");
      setNewProjectDescription("");
    } catch (err) {
      console.error("Error updating project:", err);
      // Error is already handled by the useProjects hook
    }
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    try {
      await deleteProject(projectToDelete.projectId);

      // Keep server-side search results in sync
      setServerSearchResults((prev) =>
        prev
          ? prev.filter((p) => p.projectId !== projectToDelete.projectId)
          : prev,
      );

      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (err) {
      console.error("Error deleting project:", err);
      // Error is already handled by the useProjects hook
    }
  };

  // Filter projects: use server-side results when available, otherwise filter in-memory
  const filteredProjects = React.useMemo(() => {
    // Server-side search was performed — use those results directly
    if (serverSearchResults !== null) {
      return serverSearchResults;
    }

    // No search term — show all loaded projects
    if (!searchTerm.trim()) {
      return allProjects;
    }

    // Only filter in-memory when every project is loaded; otherwise wait for
    // the server-side search to respond (avoids a misleading partial-filter flash).
    if (!allProjectsLoaded) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    return allProjects.filter(
      (project: Project) =>
        (project.projectName || "").toLowerCase().includes(searchLower) ||
        (project.projectDescription || "").toLowerCase().includes(searchLower),
    );
  }, [allProjects, searchTerm, serverSearchResults, allProjectsLoaded]);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  // Paginated slice of filtered projects for the current page
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProjects.length / DEFAULT_PROJECT_MAX_PAGE_SIZE),
  );
  const paginatedProjects = React.useMemo(() => {
    const start = currentPage * DEFAULT_PROJECT_MAX_PAGE_SIZE;
    return filteredProjects.slice(start, start + DEFAULT_PROJECT_MAX_PAGE_SIZE);
  }, [filteredProjects, currentPage]);

  // Whether there could be more projects beyond what's loaded
  const isServerSearch = serverSearchResults !== null;
  const hasNextPage = isServerSearch
    ? currentPage < totalPages - 1 || !!serverSearchNextToken
    : currentPage < totalPages - 1 || !!nextToken;

  const handlePrevPage = () => {
    setCurrentPage((p: number) => Math.max(0, p - 1));
  };

  const handleNextPage = async () => {
    if (isServerSearch) {
      if (currentPage < totalPages - 1) {
        // Navigate forward within already-fetched search results
        setCurrentPage((p: number) => p + 1);
      } else if (serverSearchNextToken) {
        // On the last local page — fetch next batch and append
        setIsSearching(true);
        try {
          const termAtRequest = searchTerm.trim();
          const result = await searchProjects(
            termAtRequest,
            serverSearchNextToken,
          );
          if (searchTermRef.current !== termAtRequest) {
            // Search term changed while request was in-flight — discard stale results
            return;
          }
          setServerSearchResults((prev) => [
            ...(prev ?? []),
            ...result.projects,
          ]);
          setServerSearchNextToken(result.nextToken ?? null);
          setCurrentPage((p: number) => p + 1);
        } catch {
          // Error already dispatched by the hook
        } finally {
          setIsSearching(false);
        }
      }
    } else if (currentPage < totalPages - 1) {
      setCurrentPage((p: number) => p + 1);
    } else if (nextToken) {
      await fetchProjects(false, nextToken);
      setCurrentPage((p: number) => p + 1);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Navigation - Same as HomePage */}
      <div className="h-14 border-b border-border bg-background flex items-center px-4 gap-4">
        {/* Left: Logo and description */}
        <div
          className="flex items-center gap-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => (window.location.href = "/")}
        >
          <img
            src="/logo.png"
            alt="TACO Logo"
            className="w-5 h-5 object-contain"
          />
          <span className="font-semibold text-foreground">TACO</span>
          <div className="w-px h-4 bg-gray-300 mx-1"></div>
          <span className="text-sm text-muted-foreground">
            Technical Architecture Composer
          </span>
        </div>

        {/* Right: My Projects, Theme, User */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => navigate("/projects")}
          >
            <Folder className="h-4 w-4" />
            My Projects
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <div className="border-b border-border bg-background">
          <div className="max-w-6xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  My Projects
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage and explore all your technical architecture projects
                </p>
              </div>

              <Button
                className="gap-2"
                onClick={() => (window.location.href = "/")}
              >
                <Plus className="h-4 w-4" />
                New Project
              </Button>
            </div>

            {/* Search and View Controls */}
            <div className="flex items-center justify-between gap-4 mt-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9 w-80 h-10 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:border-gray-400"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1 border border-border rounded-lg p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 w-8 p-0 ${
                    viewMode === "grid" ? "bg-accent" : "opacity-50"
                  }`}
                  onClick={() => setViewMode("grid")}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 w-8 p-0 ${
                    viewMode === "list" ? "bg-accent" : "opacity-50"
                  }`}
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Projects Display */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          {isLoading ||
          isSearching ||
          (!allProjectsLoaded &&
            searchTerm.trim() &&
            serverSearchResults === null) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-muted-foreground">
                {isSearching ||
                (!allProjectsLoaded &&
                  searchTerm.trim() &&
                  serverSearchResults === null)
                  ? "Searching projects..."
                  : "Loading projects..."}
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? "No projects found matching your search"
                  : "No projects yet"}
              </p>
              {!searchTerm && (
                <Button onClick={() => (window.location.href = "/")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Project
                </Button>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedProjects.map((project, index) => (
                <Card
                  key={index}
                  className="hover:shadow-md transition-shadow overflow-hidden group"
                >
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/project/${project.projectId}`)}
                  >
                    <div className="relative">
                      <ProjectThumbnail
                        projectId={project.projectId}
                        projectName={project.projectName || "Untitled Project"}
                        index={index}
                        className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    </div>

                    <div className="p-4">
                      <h3 className="font-medium text-foreground hover:text-blue-600 line-clamp-1 mb-2">
                        {project.projectName || "Untitled Project"}
                      </h3>

                      <div className="flex items-center justify-end mb-3">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-accent"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(
                                  ProjectAction.Rename,
                                  project.projectId,
                                  e,
                                )
                              }
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(
                                  ProjectAction.Delete,
                                  project.projectId,
                                  e,
                                )
                              }
                              className="text-red-600 dark:text-red-500 focus:text-red-600 dark:focus:text-red-500"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                        {project.projectDescription}
                      </p>
                      <div className="flex items-center justify-end text-xs text-muted-foreground">
                        <span>{formatDate(project.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Project Name
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {paginatedProjects.map((project: Project, index: number) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() =>
                          navigate(`/project/${project.projectId}`)
                        }
                      >
                        <div className="flex items-center">
                          <ProjectThumbnail
                            projectId={project.projectId}
                            projectName={
                              project.projectName || "Untitled Project"
                            }
                            index={index}
                            className="h-10 w-10 rounded object-cover mr-3"
                          />
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {project.projectName || "Untitled Project"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(project.updatedAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                        <div className="line-clamp-2">
                          {project.projectDescription}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-accent"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(
                                  ProjectAction.Rename,
                                  project.projectId,
                                  e,
                                )
                              }
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(
                                  ProjectAction.Delete,
                                  project.projectId,
                                  e,
                                )
                              }
                              className="text-red-600 dark:text-red-500 focus:text-red-600 dark:focus:text-red-500"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {filteredProjects.length > 0 && (currentPage > 0 || hasNextPage) && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1}
                {isServerSearch
                  ? serverSearchNextToken
                    ? ""
                    : ` of ${totalPages}`
                  : !nextToken
                    ? ` of ${totalPages}`
                    : ""}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasNextPage || isLoading || isSearching}
                className="gap-1"
              >
                {isLoading || isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
            <DialogDescription>
              Enter a new name and description for "
              {projectToRename?.projectName}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    handleRenameConfirm();
                  }
                }}
                placeholder="Enter project name"
                maxLength={nameMaxLength}
                className="border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:border-gray-400"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-right">
                {newProjectName.length}/{nameMaxLength}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                placeholder="Enter project description"
                rows={3}
                maxLength={descMaxLength}
                className="resize-none border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:border-gray-400"
              />
              <p className="text-xs text-muted-foreground text-right">
                {newProjectDescription.length}/{descMaxLength}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameConfirm}
              disabled={!newProjectName.trim()}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{projectToDelete?.projectName}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
